/**
 * dealCopy.ts — spec §3.0 / §4 "No developer strings in the UI" (deal detail
 * slice 1, §39.9): the plain-language forms of the raw values the deal page's
 * reads carry. Every mapping LOGS the raw text (spec §4: "log the raw text
 * instead"), so support can still see what the wire said. Nothing here
 * re-derives a fact: a value the map does not know renders as the honest
 * generic sentence, never as a guess at its meaning.
 */

/** The forecast suggestion rules by id (internal/modules/sales/forecast/
 *  suggest.go: RuleOmittedNoValueOrPastDue … RuleNewLead), named as a rep
 *  would say them. The server writes the audit value as "rule N: reason"
 *  (forecast_accept.go) — the reason text varies within a rule (rule 3
 *  carries the days), so the NAME is keyed by id, never parsed from it. */
const RULE_NAME: Record<number, string> = {
  1: 'no-value-or-past-due',
  2: 'add-a-close-date',
  3: 'negotiation-closing-soon',
  4: 'proposal-out',
  5: 'qualified',
  6: 'early-stage',
}

export function logRawCopy(kind: string, raw: string): void {
  // Spec §4: the raw text is logged, not shown. Debug level — it is not an
  // error, and the console is the only log the webapp has.
  console.debug(`[deal copy] ${kind}: ${raw}`)
}

/** "rule 6: Early stage" → "Suggested by Klaxiora's early-stage rule". An
 *  unknown rule id or a value not in the server's shape → the generic
 *  sentence; the raw value is logged either way. */
export function suggestionLabel(raw: string | null | undefined): string {
  if (raw == null || raw === '') return 'not set'
  logRawCopy('forecast suggestion', raw)
  const m = /^rule (\d+):/.exec(raw)
  const name = m ? RULE_NAME[Number(m[1])] : undefined
  return name ? `Suggested by Klaxiora's ${name} rule` : 'Suggested by a Klaxiora rule'
}

/** The account tile's initials (spec §3.2, "RR"): the first letter of the
 *  first two words, split on spaces and dashes — "Rolls-Royce Holdings" is
 *  "RH"; a one-word name gives one letter; '' gives ''. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/[\s\-–—]+/).filter(Boolean)
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase()
}

export type PriorityPill = { label: string; tone: 'warning' | 'neutral' }

/** The hero's priority pill (spec §3.2): High amber, Medium grey, Low and
 *  absent hidden. A stored value outside the vocabulary renders verbatim and
 *  grey — the vocab rule: never re-label a value the vocabulary does not know. */
export function priorityPill(priority: string): PriorityPill | null {
  if (!priority || priority === 'low') return null
  if (priority === 'high') return { label: 'High priority', tone: 'warning' }
  if (priority === 'medium') return { label: 'Medium priority', tone: 'neutral' }
  return { label: priority, tone: 'neutral' }
}
